import { createForm } from '@seek/forms-ui';
import { required } from '@seek/validators-js';
import {
  Dropdown,
  Card,
  Stack,
  TextField,
  Text,
  Strong,
  Box,
} from 'braid-design-system';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import industries, {
  type IndustryGroup,
  type Industry,
} from 'src/data/industries';
import { useSubmit } from 'src/hooks/submit';
import type { StoreProps } from 'src/store';
import {
  submitFinanceDetails,
  type FinancesDetails,
} from 'src/store/calculatorForm';
import { withoutHighlight } from 'src/utils';
import { calculateProfit } from 'src/utils/priceGuide';

import NavigationButton from '../../tempComponents/NavigationButton/NavigationButton';

const displayOptions = (groupIndustries: Industry[]) =>
  groupIndustries.map((industry, index) => (
    <option key={index} value={industry.value}>
      {industry.label}
    </option>
  ));

const displayOptionGroups = (groups: IndustryGroup[]) =>
  groups.map((group, index) => (
    <optgroup key={index} label={group.label}>
      {displayOptions(group.value)}
    </optgroup>
  ));

const FORM_NAME = 'financesForm';

const { FormProvider, useField } = createForm<FinancesDetails>(FORM_NAME);

interface Props {
  handleSubmit: (_: any) => any;
}

const FinancesForm = ({ handleSubmit }: Props) => {
  const { submitStatus, submit } = useSubmit(submitFinanceDetails);

  const industry = withoutHighlight(
    useField({
      id: 'industry',
      validators: [required],
    }),
  );

  const rent = withoutHighlight(
    useField({
      id: 'rent',
      validators: [required],
    }),
  );

  const revenue = withoutHighlight(
    useField({
      id: 'revenue',
      validators: [required],
    }),
  );

  const expenses = withoutHighlight(
    useField({
      id: 'expenses',
      validators: [required],
    }),
  );

  const stock = withoutHighlight(
    useField({
      id: 'stock',
      validators: [required],
    }),
  );

  return (
    <Card>
      <form onSubmit={handleSubmit(submit)}>
        <Stack space="small">
          <Dropdown
            {...industry}
            id="id"
            label="Which industry does your business belong to?"
            placeholder="Select an industry..."
            tone={industry.valid ? 'neutral' : 'critical'}
            message={!industry.valid && industry.errorMessage}
          >
            {displayOptionGroups(industries)}
          </Dropdown>
          <TextField
            {...revenue}
            id="revenue"
            type="number"
            label="Monthly revenue"
            placeholder="Monthly revenue"
            data={{ testid: 'revenue' }}
            tone={revenue.valid ? 'neutral' : 'critical'}
            message={!revenue.valid && revenue.errorMessage}
          />
          <TextField
            id="rent"
            type="number"
            label="Monthly Rent"
            placeholder="Monthly Rent"
            data={{ testid: 'rent' }}
            tone={rent.valid ? 'neutral' : 'critical'}
            message={!rent.valid && rent.errorMessage}
            {...rent}
          />

          <TextField
            {...expenses}
            id="expenses"
            type="number"
            label="Other expenses"
            secondaryLabel="per month"
            placeholder="Other expenses"
            data={{ testid: 'expenses' }}
            tone={expenses.valid ? 'neutral' : 'critical'}
            message={!expenses.valid && expenses.errorMessage}
          />

          <TextField
            {...stock}
            id="stock"
            type="number"
            label="Total value of current stock, equipment, fitout"
            placeholder="Value of stock"
            data={{ testid: 'stock' }}
            tone={stock.valid ? 'neutral' : 'critical'}
            message={
              stock.valid
                ? 'What would it cost to start your buiness from scratch?'
                : stock.errorMessage
            }
          />
          <Box marginTop="small">
            <Text>
              <Strong>Calculated Profit:</Strong> $
              {calculateProfit(revenue.value, rent.value, expenses.value)} per
              month
            </Text>
          </Box>
          <NavigationButton />
        </Stack>
      </form>
      {submitStatus?.status === 'SUCCESS' && (
        <Navigate to="/price-guide/operations" />
      )}
    </Card>
  );
};

const Finances = (props: any) => {
  const formVals = useSelector(
    ({ calculatorForm }: StoreProps) => calculatorForm,
  );
  return (
    // @ts-ignore
    <FormProvider initialValues={formVals}>
      {({ handleSubmit }) => (
        <FinancesForm {...props} handleSubmit={handleSubmit} />
      )}
    </FormProvider>
  );
};

export default Finances;
